import { useMemo } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Loader2,
  Circle,
  PlayCircle,
  CheckCircle2,
} from 'lucide-react'
import {
  useTareas,
  useCambiarEstadoTarea,
  useUltimosComentarios,
  useComentariosSinLeer,
} from '@/hooks/useTareas'
import { toast } from 'sonner'
import type { TareaCompleta, TareaComentario, EstadoTarea } from '@/types/database'
import { useUserNameResolver } from '@/core/hooks/useUserNameResolver'
import { parseISO, startOfDay, isBefore } from 'date-fns'
import { cn } from '@/lib/utils'
import { EditableDateCell } from './EditableDateCell'
import { TareaGrupoFila } from './TareaGrupoFila'
import { FechaCompletada } from './FechaCompletada'
import { InscripcionBadge } from './Inscripcion'
import { SinLeerBadge } from './SinLeerBadge'
import { useConfirmarCompletar } from './ConfirmarCompletarDialog'
import { useTareasDeGrupos } from '@/core/hooks/useTareasGrupo'
import { useTablePreferences } from '@/hooks/useTablePreferences'
import {
  agruparTareas,
  type GrupoDeTablero,
  type ItemTablero,
  type TareaConGrupo,
} from '@/core/services/tareasGrupoService'

interface TareasListaProps {
  filtroEstado?: EstadoTarea
  filtroAsignado?: string
  /** ORR-398: la lista nunca recibió este filtro, así que "Asigné" no hacía nada acá ni en mobile. */
  filtroAsignadoPor?: string
  /** La posición "Míos" del interruptor: la tarea asignada a mí. */
  filtroPropias?: string
  filtroCliente?: string
  filtroBusqueda?: string
  mostrarCompletadas?: boolean
  completadasDesde?: string | null
  completadasHasta?: string | null
  onVerDetalle?: (tarea: TareaCompleta) => void
  onEditar?: (tarea: TareaCompleta) => void
  onEliminar?: (tarea: TareaCompleta) => void
  /**
   * Con esta prop y sin filtro de cliente, las tareas de un grupo de clientes van en una sola fila
   * que se despliega. Sin ella, cada tarea es su propia fila.
   */
  onVerGrupo?: (grupo: GrupoDeTablero) => void
  // Permisos (canEdit acepta predicado por fila: ORR-200 B3 editarTareaPropia)
  canEdit?: boolean | ((tarea: TareaCompleta) => boolean)
  canDelete?: boolean
  // Acepta predicado por fila, igual que canEdit: cambiar el estado de una tarea ajena
  // lo rechaza la RPC, así que el menú no debe ofrecerlo (ORR-327, review de Codex).
  canChangeEstado?: boolean | ((tarea: TareaCompleta) => boolean)
  // Props para selección múltiple
  selectionMode?: boolean
  selectedIds?: Set<string>
  onSelectionChange?: (id: string, selected: boolean) => void
  /** Clave con la que se guardan por usuario los anchos de columna. */
  clavePreferencias?: string
}

const ESTADO_STYLES: Record<EstadoTarea, string> = {
  pendiente: 'bg-slate-100 text-slate-800',
  en_proceso: 'bg-blue-100 text-blue-800',
  completada: 'bg-green-100 text-green-800',
}

const ESTADO_LABELS: Record<EstadoTarea, string> = {
  pendiente: 'Pendiente',
  en_proceso: 'En Proceso',
  completada: 'Completada',
}

const ESTADO_ICONS: Record<EstadoTarea, typeof Circle> = {
  pendiente: Circle,
  en_proceso: PlayCircle,
  completada: CheckCircle2,
}

const PRIORIDAD_ORDER: Record<string, number> = { urgente: 0, alta: 1, media: 2, baja: 3 }

const COLUMNA_LISTA = 'lista'

const SIN_COMENTARIOS: Record<string, TareaComentario> = {}

interface ColumnasVisibles {
  seleccion: boolean
  cliente: boolean
  acciones: boolean
}

interface ColumnaDeLaLista {
  id: string
  titulo: string
  anchoBase?: number
  redimensionable: boolean
}

const ANCHO_MINIMO = 50

// Columnas del encabezado. Sin un ancho guardado, cada una toma su ancho base o, si no tiene, el
// de su contenido. Selección y acciones no se redimensionan.
const columnasDeLaLista = ({ seleccion, cliente, acciones }: ColumnasVisibles): ColumnaDeLaLista[] => [
  ...(seleccion ? [{ id: 'seleccion', titulo: '', anchoBase: 50, redimensionable: false }] : []),
  { id: 'fecha', titulo: 'Fecha límite', redimensionable: true },
  { id: 'estado', titulo: 'Estado', redimensionable: true },
  ...(cliente ? [{ id: 'cliente', titulo: 'Cliente', redimensionable: true }] : []),
  { id: 'titulo', titulo: 'Título', anchoBase: 300, redimensionable: true },
  { id: 'ultimoComentario', titulo: 'Último comentario', anchoBase: 220, redimensionable: true },
  { id: 'asignado', titulo: 'Asignado', redimensionable: true },
  { id: 'completada', titulo: 'Completada', redimensionable: true },
  ...(acciones ? [{ id: 'acciones', titulo: '', anchoBase: 50, redimensionable: false }] : []),
]

interface EncabezadoDeColumnaProps {
  columna: ColumnaDeLaLista
  ancho?: number
  onAncho: (ancho: number) => void
  onRestablecer: () => void
}

// Encabezado de una columna. Declara como ancho el guardado o, si no hay, el base. Si la columna
// se redimensiona, la franja del borde derecho cambia el ancho al arrastrarla y lo borra con doble
// clic. El arrastre parte del ancho declarado y, si no hay, del que se ve. Cuando la tabla no
// entra, el navegador reparte el espacio en proporción a los anchos declarados, y nunca achica una
// columna por debajo de su contenido.
const EncabezadoDeColumna = ({ columna, ancho, onAncho, onRestablecer }: EncabezadoDeColumnaProps) => {
  const anchoDeclarado = ancho ?? columna.anchoBase
  const empezarArrastre = (inicioX: number, encabezado: HTMLElement) => {
    const anchoInicial = anchoDeclarado ?? encabezado.getBoundingClientRect().width
    const mover = (e: MouseEvent | TouchEvent) => {
      const x = 'touches' in e ? e.touches[0].clientX : e.clientX
      onAncho(Math.max(ANCHO_MINIMO, Math.round(anchoInicial + x - inicioX)))
    }
    const soltar = () => {
      document.removeEventListener('mousemove', mover)
      document.removeEventListener('mouseup', soltar)
      document.removeEventListener('touchmove', mover)
      document.removeEventListener('touchend', soltar)
    }
    document.addEventListener('mousemove', mover)
    document.addEventListener('mouseup', soltar)
    document.addEventListener('touchmove', mover, { passive: true })
    document.addEventListener('touchend', soltar)
  }

  return (
    <TableHead className="relative" style={anchoDeclarado ? { width: anchoDeclarado } : undefined}>
      {columna.titulo}
      {columna.redimensionable && (
        <div
          aria-hidden="true"
          onMouseDown={(e) => {
            e.preventDefault()
            empezarArrastre(e.clientX, e.currentTarget.parentElement as HTMLElement)
          }}
          onTouchStart={(e) =>
            empezarArrastre(e.touches[0].clientX, e.currentTarget.parentElement as HTMLElement)
          }
          onDoubleClick={onRestablecer}
          className="group absolute right-0 top-0 flex h-full w-4 cursor-col-resize touch-none select-none items-center justify-end"
          title="Arrastra para redimensionar. Doble clic para restablecer."
        >
          <div className="h-full w-[3px] rounded-full bg-transparent transition-colors group-hover:bg-primary/50 group-active:bg-primary" />
        </div>
      )}
    </TableHead>
  )
}

export function TareasLista({
  filtroEstado,
  filtroAsignado,
  filtroAsignadoPor,
  filtroPropias,
  filtroCliente,
  filtroBusqueda,
  onVerDetalle,
  onEditar,
  onEliminar,
  onVerGrupo,
  mostrarCompletadas = false,
  completadasDesde,
  completadasHasta,
  canEdit = true,
  canDelete = true,
  canChangeEstado = true,
  selectionMode = false,
  selectedIds = new Set(),
  onSelectionChange,
  clavePreferencias = 'tareas-lista',
}: TareasListaProps) {
  const puedeEditarFila = (tarea: TareaCompleta) =>
    typeof canEdit === 'function' ? canEdit(tarea) : canEdit

  const puedeCambiarEstadoFila = (tarea: TareaCompleta) =>
    typeof canChangeEstado === 'function' ? canChangeEstado(tarea) : canChangeEstado

  // Ver el comentario del TableHeader: por qué estas dos columnas no siempre van.
  const mostrarColumnaCliente = !filtroCliente
  const mostrarColumnaAcciones = Boolean(canEdit) || Boolean(canDelete)
  const filaAbrible = Boolean(onVerDetalle) && !selectionMode

  const { data: tareasRaw = [], isLoading } = useTareas({
    estado: filtroEstado,
    asignado_a: filtroAsignado,
    asignado_por: filtroAsignadoPor,
    propias_de: filtroPropias,
    cliente_id: filtroCliente,
    busqueda: filtroBusqueda,
    mostrarCompletadas,
    completadasDesde: mostrarCompletadas ? completadasDesde : undefined,
    completadasHasta: mostrarCompletadas ? completadasHasta : undefined,
  })

  // Server-side filtering handles completadas — no client-side filter needed
  const tareas = tareasRaw

  const tareasSorted = useMemo(() => {
    return [...tareas].sort((a, b) => {
      // Sort by fecha_limite ascending (nulls last)
      const fechaA = a.fecha_limite ? parseISO(a.fecha_limite).getTime() : Infinity
      const fechaB = b.fecha_limite ? parseISO(b.fecha_limite).getTime() : Infinity
      if (fechaA !== fechaB) return fechaA - fechaB

      // Same date: urgentes first
      const prioA = PRIORIDAD_ORDER[a.prioridad ?? 'media'] ?? 2
      const prioB = PRIORIDAD_ORDER[b.prioridad ?? 'media'] ?? 2
      return prioA - prioB
    })
  }, [tareas])

  // El listado agrupa con `agruparTareas`, el mismo criterio que el tablero, en una sola columna.
  // El avance de un grupo cuenta sus completadas, que la consulta del listado puede no traer.
  const agrupar = Boolean(onVerGrupo) && !filtroCliente
  const clavesDeGrupo = useMemo(
    () =>
      agrupar
        ? (tareas as TareaConGrupo[]).flatMap((t) => (t.grupo_clave ? [t.grupo_clave] : []))
        : [],
    [tareas, agrupar]
  )
  const { data: tareasDeGrupos = [] } = useTareasDeGrupos(clavesDeGrupo)
  const items = useMemo<ItemTablero[]>(
    () =>
      agrupar
        ? agruparTareas(tareasSorted, tareasDeGrupos, () => COLUMNA_LISTA, [COLUMNA_LISTA])
        : tareasSorted.map((tarea) => ({ tipo: 'tarea', tarea, columna: COLUMNA_LISTA })),
    [agrupar, tareasSorted, tareasDeGrupos]
  )

  const cambiarEstado = useCambiarEstadoTarea()
  const { confirmar, dialogo } = useConfirmarCompletar()
  const resolveNombre = useUserNameResolver()
  const today = useMemo(() => startOfDay(new Date()), [])

  // ORR-259: se piden solo las tareas que tienen al menos un comentario. La vista ya
  // trae `cantidad_comentarios`, así que filtrar acá evita mandar los ids de todas.
  const idsConComentario = useMemo(
    () =>
      tareas.reduce<string[]>((acc, t) => {
        if ((t.cantidad_comentarios ?? 0) > 0) acc.push(t.id)
        return acc
      }, []),
    [tareas]
  )
  const { data: ultimosComentarios = SIN_COMENTARIOS } = useUltimosComentarios(idsConComentario)
  const sinLeer = useComentariosSinLeer(ultimosComentarios)

  const columnas = useMemo(
    () =>
      columnasDeLaLista({
        seleccion: selectionMode,
        cliente: mostrarColumnaCliente,
        acciones: mostrarColumnaAcciones,
      }),
    [selectionMode, mostrarColumnaCliente, mostrarColumnaAcciones]
  )
  const { columnSizing, saveColumnSizing } = useTablePreferences(clavePreferencias)
  const guardarAncho = (id: string, ancho: number) =>
    saveColumnSizing((anteriores) => ({ ...anteriores, [id]: ancho }))
  const restablecerAncho = (id: string) =>
    saveColumnSizing((anteriores) => {
      const restantes = { ...anteriores }
      delete restantes[id]
      return restantes
    })

  const handleCambiarEstado = (tarea: TareaCompleta, nuevoEstado: EstadoTarea) => {
    if (!puedeCambiarEstadoFila(tarea)) return
    if (tarea.estado === nuevoEstado) return

    confirmar(nuevoEstado, () =>
      cambiarEstado.mutate(
        {
          id: tarea.id,
          estado: nuevoEstado,
          estadoAnterior: tarea.estado as EstadoTarea,
        },
        {
          onSuccess: () => {
            toast.success(`Tarea movida a "${ESTADO_LABELS[nuevoEstado]}"`)
          },
          onError: () => {
            toast.error('Error al cambiar el estado')
          },
        }
      )
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (tareas.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <p className="text-lg">No hay tareas</p>
        <p className="text-sm">Las tareas que crees aparecerán aquí</p>
      </div>
    )
  }

  return (
    <div className="rounded-md border">
      {dialogo}
      <Table>
        {/* ORR-348: estas dos columnas no van cuando la lista está embebida y filtrada por un
            cliente, y cada una sobra por su cuenta —no por el ancho: Cliente repite el mismo
            valor en todas las filas, y la de acciones queda con un único ítem, "Ver detalle",
            que es exactamente lo que ya hace el click en la fila. El desborde que las destapó
            lo terminó de cerrar ORR-350, ensanchando el panel. */}
        <TableHeader>
          <TableRow>
            {columnas.map((columna) => (
              <EncabezadoDeColumna
                key={columna.id}
                columna={columna}
                ancho={columnSizing[columna.id]}
                onAncho={(ancho) => guardarAncho(columna.id, ancho)}
                onRestablecer={() => restablecerAncho(columna.id)}
              />
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            if (item.tipo === 'grupo') {
              return (
                <TareaGrupoFila
                  key={`grupo:${item.grupo.clave}`}
                  grupo={item.grupo}
                  mostrarColumnaAcciones={mostrarColumnaAcciones}
                  selectionMode={selectionMode}
                  selectedIds={selectedIds}
                  onSelectionChange={onSelectionChange}
                  onVerGrupo={onVerGrupo}
                  onVerDetalle={onVerDetalle}
                  onCambiarEstado={handleCambiarEstado}
                  puedeEditarFila={puedeEditarFila}
                  puedeCambiarEstadoFila={puedeCambiarEstadoFila}
                  sinLeer={sinLeer}
                />
              )
            }
            const { tarea } = item
            const fechaLimite = tarea.fecha_limite
              ? startOfDay(parseISO(`${tarea.fecha_limite}T00:00:00`))
              : null
            const estaVencida =
              fechaLimite && isBefore(fechaLimite, today) && tarea.estado !== 'completada'

            return (
              <TableRow
                key={tarea.id}
                data-tarea-id={tarea.id}
                className={cn(
                  'hover:bg-muted/50',
                  onVerDetalle && 'cursor-pointer',
                  estaVencida && 'bg-red-50 dark:bg-red-950/30',
                  selectedIds.has(tarea.id) && 'bg-accent/10'
                )}
                onClick={() => !selectionMode && onVerDetalle?.(tarea)}
                // ORR-348: sin esto la fila se abre solo con mouse — un `tr` con onClick no es
                // focusable ni se activa con el teclado. Y como Radix devuelve el foco al
                // elemento que abrió el diálogo, sin nada focusable el foco terminaba en el
                // `body` y había que tabular de nuevo desde el principio del panel.
                tabIndex={filaAbrible ? 0 : undefined}
                onKeyDown={
                  filaAbrible
                    ? (e) => {
                        if (e.target !== e.currentTarget) return
                        if (e.key !== 'Enter' && e.key !== ' ') return
                        e.preventDefault()
                        onVerDetalle?.(tarea)
                      }
                    : undefined
                }
              >
                {selectionMode && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.has(tarea.id)}
                      onCheckedChange={(checked) =>
                        onSelectionChange?.(tarea.id, checked === true)
                      }
                      aria-label={`Seleccionar ${tarea.titulo}`}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <EditableDateCell
                    tareaId={tarea.id}
                    fechaLimite={tarea.fecha_limite}
                    estado={tarea.estado}
                    canEdit={puedeEditarFila(tarea)}
                  />
                </TableCell>
                <TableCell>
                  {puedeCambiarEstadoFila(tarea) ? (
                    <div onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-auto p-0 hover:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
                          >
                            <Badge
                              variant="secondary"
                              className={cn(
                                'text-xs cursor-pointer hover:opacity-80 transition-opacity',
                                ESTADO_STYLES[tarea.estado as EstadoTarea]
                              )}
                            >
                              {ESTADO_LABELS[tarea.estado as EstadoTarea]}
                            </Badge>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          {(['pendiente', 'en_proceso', 'completada'] as EstadoTarea[]).map(
                            (estado) => {
                              const Icon = ESTADO_ICONS[estado]
                              const isCurrentState = tarea.estado === estado
                              return (
                                <DropdownMenuItem
                                  key={estado}
                                  onClick={() => handleCambiarEstado(tarea, estado)}
                                  className={cn(isCurrentState && 'bg-muted')}
                                >
                                  <Icon
                                    className={cn(
                                      'mr-2 h-4 w-4',
                                      estado === 'pendiente' && 'text-slate-500',
                                      estado === 'en_proceso' && 'text-blue-500',
                                      estado === 'completada' && 'text-green-500'
                                    )}
                                  />
                                  {ESTADO_LABELS[estado]}
                                  {isCurrentState && (
                                    <span className="ml-auto text-xs text-muted-foreground">
                                      actual
                                    </span>
                                  )}
                                </DropdownMenuItem>
                              )
                            }
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ) : (
                    <Badge
                      variant="secondary"
                      className={cn('text-xs', ESTADO_STYLES[tarea.estado as EstadoTarea])}
                    >
                      {ESTADO_LABELS[tarea.estado as EstadoTarea]}
                    </Badge>
                  )}
                </TableCell>
                {mostrarColumnaCliente && (
                  <TableCell>
                    {tarea.cliente_nombre ? (
                      tarea.cliente_nombre
                    ) : (
                      <span className="text-muted-foreground">Sin asignar</span>
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <div className="flex flex-col">
                    <span className="flex items-center gap-2">
                      <span className="font-medium line-clamp-1">
                        {tarea.titulo}
                      </span>
                      {tarea.tipo === 'inscripcion' && <InscripcionBadge />}
                    </span>
                    {tarea.tipo_impuesto && (
                      <span className="text-xs text-muted-foreground">
                        {tarea.tipo_impuesto} - {tarea.vencimiento_periodo}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {ultimosComentarios[tarea.id] ? (
                    <span className="flex items-center gap-2">
                      {sinLeer.has(tarea.id) && <SinLeerBadge />}
                      <span
                        className={cn('line-clamp-1 text-sm', sinLeer.has(tarea.id) && 'font-semibold')}
                        title={`${resolveNombre(ultimosComentarios[tarea.id].autor)}: ${ultimosComentarios[tarea.id].contenido}`}
                      >
                        {ultimosComentarios[tarea.id].contenido}
                      </span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {tarea.asignado_a ? (
                    resolveNombre(tarea.asignado_a)
                  ) : (
                    <span className="text-muted-foreground">Sin asignar</span>
                  )}
                </TableCell>
                <TableCell>
                  <FechaCompletada fecha={tarea.fecha_completada} />
                </TableCell>
                {mostrarColumnaAcciones && (
                <TableCell>
                  {/* Los tres ítems del menú son condicionales: sin ninguno, el botón abría
                      un menú vacío. Mismo problema que las affordances de detalle. */}
                  {(onVerDetalle || puedeEditarFila(tarea) || canDelete) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {onVerDetalle && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation()
                            onVerDetalle(tarea)
                          }}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          Ver detalle
                        </DropdownMenuItem>
                      )}
                      {puedeEditarFila(tarea) && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation()
                            onEditar?.(tarea)
                          }}
                        >
                          <Edit className="mr-2 h-4 w-4" />
                          Editar
                        </DropdownMenuItem>
                      )}
                      {canDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={(e) => {
                              e.stopPropagation()
                              onEliminar?.(tarea)
                            }}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Eliminar
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  )}
                </TableCell>
                )}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
